import { EPasswordField } from "../enum/e-password-field";
import { AValidator } from "./a-validator";
import { fromEvent } from "rxjs";
import { FieldRuleModel } from "../../../utils/typescript/model/field/field-rule.model";
import { PasswordStepConfigModel } from "../../../utils/typescript/model/password/password-step-config.model";
import { PasswordStrengthModel } from "../../../utils/typescript/model/password/password-strength.model";
import { FieldConfigModel } from "../../../utils/typescript/model/field/field-config.model";

declare let passwordRequirements: Map<string, string>;
declare let miscellaneous: Map<string, string>;
export class PasswordResetValidator extends AValidator {
  protected formElement: HTMLFormElement;
  protected elements: Map<EPasswordField, unknown> = new Map();
  protected stepConfig: PasswordStepConfigModel[];
  protected passwordStrength: PasswordStrengthModel;
  protected ruleset: FieldConfigModel[];
  protected tempStrength = 0;
  protected isPasswordRequirementsHintEnabled = false;
  protected isPasswordStrengthIndicatorEnabled = false;
  protected isShowHidePasswordFunctionEnabled = false;

  constructor(formElement: HTMLFormElement, ruleset: FieldConfigModel[]) {
    super();

    this.formElement = formElement;
    this.ruleset = ruleset;
    this.buildElementList();

    this.buildPasswordStrength();
    this.buildStepConfig();

    this.enableShowHidePasswordFunction();
    this.enablePasswordStrengthIndicator();
    this.enablePasswordRequirementsHint();
  }

  getValidationFunctions() {
    return [
      // need to bind, because these methods used as callback
      this.validationNoEmptyStrings.bind(this),
      this.validationPasswordEquality.bind(this),
      this.validationValidationsFulfilled.bind(this),
    ];
  }

  protected validationNoEmptyStrings(
    setFieldHintCb: (
      errorNumber: Array<number>,
      element: Array<HTMLElement>,
    ) => void,
  ) {
    const newPasswordElement = this.elements.get(
      EPasswordField.NEW_PASSWORD,
    ) as HTMLInputElement;
    const confirmNewPasswordElement = this.elements.get(
      EPasswordField.CONFIRM_PASSWORD,
    ) as HTMLInputElement;

    if (
      newPasswordElement.value === "" ||
      confirmNewPasswordElement.value === ""
    ) {
      if (newPasswordElement.value === "") {
        setFieldHintCb([1], [newPasswordElement]);
      }

      if (confirmNewPasswordElement.value === "") {
        setFieldHintCb([1], [confirmNewPasswordElement]);
      }
    }
  }

  protected validationPasswordEquality(
    setFieldHintCb: (
      errorNumber: Array<number>,
      element: Array<HTMLElement>,
    ) => void,
  ) {
    const newPasswordElement = this.elements.get(
      EPasswordField.NEW_PASSWORD,
    ) as HTMLInputElement;
    const confirmNewPasswordElement = this.elements.get(
      EPasswordField.CONFIRM_PASSWORD,
    ) as HTMLInputElement;

    if (newPasswordElement.value !== confirmNewPasswordElement.value) {
      setFieldHintCb([2], [confirmNewPasswordElement]);
    }
  }

  protected validationValidationsFulfilled(
    setFieldHintCb: (
      errorNumber: Array<number>,
      element: Array<HTMLElement>,
    ) => void,
  ) {
    const newPasswordElement = this.elements.get(
      EPasswordField.NEW_PASSWORD,
    ) as HTMLInputElement;

    if (this.passwordStrength.strength < this.passwordStrength.validations) {
      setFieldHintCb([3], [newPasswordElement]);
    }
  }

  protected buildElementList() {
    const list = new Map();
    list.set(
      EPasswordField.PASSWORD_RESET_CONTAINER,
      this.formElement.querySelector(".password-reset-container"),
    );

    // password validation box
    list.set(
      EPasswordField.PASSWORD_VALIDATION_BOX,
      list
        .get(EPasswordField.PASSWORD_RESET_CONTAINER)
        .querySelector(".password-validation-box"),
    );

    // strength indicator
    list.set(
      EPasswordField.PASSWORD_STRENGTH_INDICATOR,
      list
        .get(EPasswordField.PASSWORD_RESET_CONTAINER)
        .querySelector(".password-strength-indicator"),
    );
    list.set(
      EPasswordField.HINT_ELEMENT,
      list
        .get(EPasswordField.PASSWORD_STRENGTH_INDICATOR)
        .querySelector(".hint"),
    );
    list.set(
      EPasswordField.HINT_TEXT,
      list.get(EPasswordField.HINT_ELEMENT).querySelector(".hint-text"),
    );
    list.set(
      EPasswordField.HINT_STRENGTH,
      list.get(EPasswordField.HINT_ELEMENT).querySelector(".hint-strength"),
    );
    list.set(
      EPasswordField.STEP_ELEMENT,
      list
        .get(EPasswordField.PASSWORD_STRENGTH_INDICATOR)
        .querySelector(".bar .step"),
    );

    // password inputs
    list.set(
      EPasswordField.NEW_PASSWORD,
      list
        .get(EPasswordField.PASSWORD_RESET_CONTAINER)
        .querySelector("#newPassword"),
    );
    list.set(
      EPasswordField.CONFIRM_PASSWORD,
      list
        .get(EPasswordField.PASSWORD_RESET_CONTAINER)
        .querySelector("#confirmNewPassword"),
    );

    // errors
    list.set(
      EPasswordField.ERROR_LIST_ELEMENTS,
      list
        .get(EPasswordField.PASSWORD_RESET_CONTAINER)
        .querySelectorAll(".hint-list"),
    );

    // password show/hide element
    list.set(
      EPasswordField.PASSWORD_SHOW_HIDE_ELEMENTS,
      list
        .get(EPasswordField.PASSWORD_RESET_CONTAINER)
        .querySelectorAll(".show-hide-password"),
    );

    this.elements = list;
  }

  protected buildStepConfig() {
    if (typeof miscellaneous === "undefined")
      console.warn(
        "password-reset-validator.ts: No miscellaneous Map available.",
      );

    const redRule = this.generateStepRule(
      0,
      33,
      "red",
      typeof miscellaneous === "undefined"
        ? "Ihr Passwort ist nicht sicher!"
        : miscellaneous.get("weak-hint") || "",
      typeof miscellaneous === "undefined"
        ? "Schwach"
        : miscellaneous.get("weak") || "",
    );

    const yellowRule = this.generateStepRule(
      34,
      66,
      "yellow",
      typeof miscellaneous === "undefined"
        ? "Ihr Passwort erfüllt noch nicht alle Kriterien"
        : miscellaneous.get("mediocre-hint") || "",
      typeof miscellaneous === "undefined"
        ? "Mittelmäßig"
        : miscellaneous.get("mediocre") || "",
    );

    const greenRule = this.generateStepRule(
      67,
      100,
      "green",
      typeof miscellaneous === "undefined"
        ? "Ihr Passwort ist sehr sicher!"
        : miscellaneous.get("strong-hint") || "",
      typeof miscellaneous === "undefined"
        ? "Stark"
        : miscellaneous.get("strong") || "",
    );

    this.stepConfig = [redRule, yellowRule, greenRule];
  }

  protected generateStepRule(
    min: number,
    max: number,
    color: string,
    hint: string,
    shortHint: string,
  ) {
    const rule = new PasswordStepConfigModel();
    rule.min = min;
    rule.max = max;
    rule.color = color;
    rule.hint = hint;
    rule.shortHint = shortHint;

    return rule;
  }

  protected buildPasswordStrength() {
    const strength = new PasswordStrengthModel();
    strength.strength = 0;
    strength.strengthInPercent = 0;
    strength.validations = 0;

    const passwordRuleset = this.ruleset.find(
      (field) => field.fieldName === "newPassword",
    );
    if (passwordRuleset) strength.validations += passwordRuleset.rules.length;

    this.passwordStrength = strength;
  }

  protected updatePasswordRequirementsHint() {
    if (!this.isPasswordRequirementsHintEnabled) {
      return;
    }

    const passwordRuleset = this.ruleset.find(
      (field) => field.fieldName === "newPassword",
    );
    if (passwordRuleset) {
      for (const rule of passwordRuleset.rules) {
        this.updateRule(rule);
      }
    }
  }

  protected updateRule(rule: FieldRuleModel) {
    const validation = (
      this.elements.get(
        EPasswordField.PASSWORD_VALIDATION_BOX,
      ) as HTMLDivElement
    ).querySelector("." + rule.ruleShortName);
    const passwordCheckElement = validation?.querySelector(".password-check");
    const icon = validation?.querySelector(".icon i");

    if (!passwordCheckElement || !icon) return;

    if (rule.passed) {
      passwordCheckElement.classList.add("valid");
      passwordCheckElement.classList.remove("not-valid");

      icon.className = "";
      icon.classList.add("fal");
      icon.classList.add("fa-check");
    } else {
      passwordCheckElement.classList.add("not-valid");
      passwordCheckElement.classList.remove("valid");

      icon.className = "";
      icon.classList.add("fal");
      icon.classList.add("fa-times");
    }
  }

  protected updatePasswordStrengthHint() {
    const hintTextElement = this.elements.get(
      EPasswordField.HINT_TEXT,
    ) as HTMLDivElement;
    const hintStrengthElement = this.elements.get(
      EPasswordField.HINT_STRENGTH,
    ) as HTMLDivElement;
    const stepElement = this.elements.get(
      EPasswordField.STEP_ELEMENT,
    ) as HTMLDivElement;

    const strengthResultInPercent =
      (this.passwordStrength.strength / this.passwordStrength.validations) *
      100;
    this.passwordStrength.strengthInPercent = strengthResultInPercent;
    stepElement.style.width = "" + Math.round(strengthResultInPercent) + "%";

    for (const config of this.stepConfig) {
      if (
        !(
          strengthResultInPercent >= config.min &&
          strengthResultInPercent <= config.max
        )
      ) {
        continue;
      }

      stepElement.className = "";
      stepElement.classList.add("step");
      stepElement.classList.add(config.color);

      hintTextElement.textContent = config.hint;

      hintStrengthElement.className = "";
      hintStrengthElement.classList.add("hint-strength", config.color);
      hintStrengthElement.textContent = config.shortHint;
    }
  }

  protected enablePasswordRequirementsHint() {
    this.isPasswordRequirementsHintEnabled = true;

    const validationBoxElement = this.elements.get(
      EPasswordField.PASSWORD_VALIDATION_BOX,
    ) as HTMLDivElement;
    validationBoxElement.classList.remove("hidden");

    const template = validationBoxElement.querySelector(".check"); // use .check as template and remove it later
    if (template) {
      const passwordRuleset = this.ruleset.filter(
        (field) => field.fieldName === "newPassword",
      );
      for (const fieldConfig of passwordRuleset) {
        for (const rule of fieldConfig.rules) {
          this.addVisibleRule(rule, template);
        }
      }
      template.remove();
    }
  }

  protected addVisibleRule(rule: FieldRuleModel, template: Element) {
    const validation = template.cloneNode(true) as HTMLElement;
    const description = validation?.querySelector(
      ".description",
    ) as HTMLDivElement;

    validation.classList.add(rule.ruleShortName);

    if (typeof passwordRequirements === "undefined")
      console.warn(
        "password-reset-validator.ts: No passwordRequirements Map available.",
      );
    description.textContent =
      typeof passwordRequirements === "undefined"
        ? rule.ruleName
        : passwordRequirements.get(rule.ruleShortName) || "";

    (
      this.elements.get(
        EPasswordField.PASSWORD_VALIDATION_BOX,
      ) as HTMLDivElement
    ).append(validation);
  }

  protected validatePasswordStrength(passwordInputElement: HTMLInputElement) {
    const password = passwordInputElement.value;

    if (password.length > 0) {
      (
        this.elements.get(
          EPasswordField.PASSWORD_STRENGTH_INDICATOR,
        ) as HTMLDivElement
      ).classList.remove("invisible");
    }

    this.tempStrength = 0;

    const passwordRuleset = this.ruleset.find(
      (field) => field.fieldName === "newPassword",
    );
    if (passwordRuleset) {
      for (const rule of passwordRuleset.rules) {
        this.checkRule(rule, password);
      }
    }

    this.passwordStrength.strength = this.tempStrength;

    this.updatePasswordStrengthHint();
    this.updatePasswordRequirementsHint();
  }

  protected enablePasswordStrengthIndicator() {
    this.isPasswordStrengthIndicatorEnabled = true;
    (
      this.elements.get(
        EPasswordField.PASSWORD_STRENGTH_INDICATOR,
      ) as HTMLDivElement
    ).classList.remove("hidden");

    fromEvent(
      <HTMLInputElement>this.elements.get(EPasswordField.NEW_PASSWORD),
      "input",
    ).subscribe(() =>
      this.validatePasswordStrength(
        this.elements.get(EPasswordField.NEW_PASSWORD) as HTMLInputElement,
      ),
    );
  }

  protected checkRule(rule: FieldRuleModel, password: string) {
    if (password.match(rule.regex)) {
      this.tempStrength++;
      rule.passed = true;
    } else {
      rule.passed = false;
    }
  }

  protected enableShowHidePasswordFunction() {
    this.isShowHidePasswordFunctionEnabled = true;

    if (typeof miscellaneous === "undefined")
      console.warn(
        "password-reset-validator.ts: No miscellaneous Map available.",
      );

    for (const element of this.elements.get(
      EPasswordField.PASSWORD_SHOW_HIDE_ELEMENTS,
    ) as HTMLDivElement[]) {
      element.classList.remove("hidden");

      fromEvent(element, "mousedown").subscribe((event: Event) => {
        event.preventDefault(); // prevent loosing focus from input element

        const newPasswordField = element.parentElement?.querySelector("input");
        if (!newPasswordField) return;

        if (newPasswordField && newPasswordField.type === "password") {
          newPasswordField.type = "text";
          element.textContent =
            typeof miscellaneous === "undefined"
              ? "ausblenden"
              : miscellaneous.get("hide") || "ausblenden";
        } else {
          newPasswordField.type = "password";
          element.textContent =
            typeof miscellaneous === "undefined"
              ? "anzeigen"
              : miscellaneous.get("show") || "anzeigen";
        }

        newPasswordField.focus();
      });
    }
  }
}
